import * as React from "react";

/**
 * Initials avatar (DS Avatar): lavender with purple initials by default. Speaker colors (1–5) stay
 * consistent for a person across transcript, notes and owners.
 */
export interface AvatarProps extends React.HTMLAttributes<HTMLSpanElement> {
  name?: string;
  initials?: string;
  /** 1–5 → the --speaker-1 … --speaker-5 tokens */
  speaker?: 1 | 2 | 3 | 4 | 5;
  /** Any CSS color, overrides speaker */
  color?: string;
  /** px, default 28 */
  size?: number;
  square?: boolean;
  /** Ring color, e.g. "var(--speaker-5)" for the active speaker */
  ring?: string;
}

/** Font size and line height for the text in a `size`-px circle. The line height takes the circle's
 * odd/even parity, so flexbox centres it on a whole pixel instead of rounding it up by half. */
export function avatarTextStyle(size: number, fontSize: number): React.CSSProperties {
  return { fontSize, lineHeight: fontSize + ((size - fontSize) % 2) + "px" };
}

export function Avatar({
  name = "",
  initials,
  speaker,
  color,
  size = 28,
  square,
  ring,
  className,
  style,
  ...rest
}: AvatarProps): JSX.Element {
  const ini =
    initials ||
    name
      .split(" ")
      .map((s) => s.charAt(0))
      .join("")
      .slice(0, 2)
      .toUpperCase();
  const bg = color || (speaker ? "var(--speaker-" + speaker + ")" : undefined);
  return (
    <span
      title={name || undefined}
      className={["gk-avatar", square && "gk-avatar--square", className].filter(Boolean).join(" ")}
      style={{
        width: size,
        height: size,
        ...avatarTextStyle(size, Math.max(9, Math.round(size * 0.37))),
        background: bg,
        color: bg ? "var(--avatar-fg)" : undefined,
        boxShadow: ring ? "0 0 0 3px " + ring : undefined,
        ...style,
      }}
      {...rest}
    >
      <span className="gk-avatar__initials">{ini}</span>
    </span>
  );
}
